import { useMemo } from 'react';

import type { MappedFeature, MappedFeatureProperties } from '@/types';
import type { Feature, FeatureCollection, Geometry, Point } from 'geojson';

const usePrepareGeojson = <T, G extends Geometry = Point>(
  data: T[] | undefined,
  mapper: (item: T) => MappedFeature<G> | null
): FeatureCollection<G> => {
  const preparedGeojson = useMemo((): FeatureCollection<G> => {
    if (!data) {
      return { type: 'FeatureCollection', features: [] };
    }

    const features = data
      ?.map((item): Feature<G, MappedFeatureProperties> | null => {
        const mappedItem = mapper(item);

        if (mappedItem === null) {
          return null;
        }

        return {
          type: 'Feature',
          geometry: mappedItem.geometry,
          properties: mappedItem.properties,
        };
      })
      .filter((feature): feature is Feature<G, MappedFeatureProperties> => feature !== null);

    return {
      type: 'FeatureCollection',
      features,
    };
  }, [data, mapper]);

  return preparedGeojson;
};

export default usePrepareGeojson;
